import React, { useEffect, useState } from 'react';
import type { CateringAlertSettings } from '../../types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const DEFAULT_CATERING_ALERTS: CateringAlertSettings = { enabled: true, emails: [] };

/** Splits a comma / newline separated list into trimmed, de-duplicated entries. */
export function parseEmailList(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean))];
}

export function invalidEmails(emails: string[]): string[] {
  return emails.filter((e) => !EMAIL_RE.test(e));
}

interface Props {
  value: CateringAlertSettings | undefined;
  onChange: (next: CateringAlertSettings) => void;
}

/**
 * Enable switch + recipient list for "new catering request" emails (sent by the
 * onCateringInquiryCreated Cloud Function). Used on the Settings page and the Catering page;
 * both write `integration_settings/main.cateringAlerts`.
 */
const CateringAlertsFields: React.FC<Props> = ({ value, onChange }) => {
  const settings = value ?? DEFAULT_CATERING_ALERTS;
  const [text, setText] = useState(settings.emails.join(', '));

  // Keep the text box in sync when settings load/refresh from outside.
  useEffect(() => {
    if (parseEmailList(text).join(',') !== settings.emails.join(',')) setText(settings.emails.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.emails.join(',')]);

  const bad = invalidEmails(parseEmailList(text));

  return (
    <div>
      <label className="settings-switch cursor-pointer">
        <span className="text-sm font-medium text-slate-800">Email me when a customer requests a catering quote</span>
        <input
          type="checkbox"
          checked={settings.enabled}
          onChange={(e) => onChange({ ...settings, enabled: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
        />
      </label>
      <div className="settings-field settings-span">
        <label htmlFor="cateringAlertEmails" className="block text-sm font-medium text-slate-800">
          Send alerts to
        </label>
        <textarea
          id="cateringAlertEmails"
          rows={2}
          value={text}
          disabled={!settings.enabled}
          onChange={(e) => {
            setText(e.target.value);
            onChange({ ...settings, emails: parseEmailList(e.target.value) });
          }}
          placeholder="events@kingspark.co.nz, manager@kingspark.co.nz"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 text-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 disabled:bg-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          Separate multiple addresses with commas. The email includes all the customer&apos;s answers; replying goes to the customer when they gave an email.
        </p>
        {settings.enabled && bad.length > 0 && (
          <p className="mt-1 text-xs text-red-600">Check these addresses: {bad.join(', ')}</p>
        )}
        {settings.enabled && settings.emails.length === 0 && (
          <p className="mt-1 text-xs text-amber-700">No recipients yet — requests will still appear on the Catering page.</p>
        )}
      </div>
    </div>
  );
};

export default CateringAlertsFields;
